// affectPalette.js
// -----------------------------------------------------------------------------
// CANONICAL PALETTE KEYS (Phase 4D Chunk 3, Fork-2 unification):
//   neutral | positive | negative | uncertain | directive | cautious |
//   sarcastic | intense | surprise | uncategorised
//
// These keys are the same 9-bucket `categorizedEmotion` vocabulary the
// server's voice-rendering pipeline uses (see
// `server/services/voiceRendering/categoryLookup.js`) plus an `uncategorised`
// sentinel for the long tail. The server's hover-panel composer (Chunk 4)
// hands the client `paletteId` strings from THIS keyspace; the client looks
// up wash/surface/accent/glyph directly. No translation layer.
//
// LEGACY DISPLAY-LABEL ALIASES:
//   The previous palette was keyed on raw author-emotion labels (strategic,
//   grouchy, frustrated, supportive, informative, teasing, apologetic,
//   testy, relieved, neutral). `getPalette()` continues to accept those
//   strings via the LEGACY_LABEL_TO_BUCKET map below, so the dev-only
//   `mockCharacters.js` fixtures (which still author display strings like
//   `strategic` / `frustrated` for tone-reference purposes) render
//   unchanged. Production paths get bucket ids directly from the server.
//
// Field meanings:
//   wash     — the dominant background tint behind the whole panel.
//   surface  — the on-content overlay (sits *over* the wash).
//   accent   — affect labels, display quote-mark, sparkline, portrait, etc.
//   glyph    — key into motifGlyphs.jsx; selects the decorative corner motif.
//   vibe     — human-readable note explaining the intent of the palette.
//
// Spec sources:
//   docs/journal/PHASE_4D_VA_HOVER_PANEL_VISUAL_DESIGN_MOCKUP.md
//   docs/journal/PHASE_4D_HOVER_PANEL_SERVER_SIDE_IMPLEMENTATION_PLAN_2026-05-17.md
//   server/services/characterContext/affectVocabulary.js (server-side mate)
// -----------------------------------------------------------------------------

const affectPalette = {
  // Bucket: neutral — the thin-card-safe fallback and the corpus-baseline
  // tint. Quiet, undefined.
  neutral: {
    wash: '#a8a59c',   // warm grey
    surface: '#f7f4ee',   // cream
    accent: '#3a3833',   // charcoal
    glyph: 'circle',
    vibe: 'quiet, undefined; the thin-card fallback',
  },

  // Bucket: positive — supportive, warm, grounded. (Previously: `supportive`.)
  positive: {
    wash: '#8aa68a',   // sage green
    surface: '#f4f1e8',   // linen
    accent: '#3f5a3f',   // forest
    glyph: 'leaf',
    vibe: 'warm, grounded; like an afternoon garden',
  },

  // Bucket: negative — irritated / aggrieved. Warm-grouchy terracotta, not
  // angry-red. Aggregates grouchy / apologetic / testy / disappointed /
  // disgusted etc. (Previously: `grouchy`.)
  negative: {
    wash: '#a85f4e',   // terracotta dust
    surface: '#fbf2e9',   // cream
    accent: '#8a3a1f',   // burnt sienna
    glyph: 'thornBranch',
    vibe: 'warm-grouchy, not angry-red; weathered terracotta',
  },

  // Bucket: uncertain — soft, retreating; dove-grey reads as not-yet-resolved.
  // (Previously occupied by `apologetic`.)
  uncertain: {
    wash: '#a89b8c',   // dove grey
    surface: '#f7f4ee',   // pale cream
    accent: '#5a4a3a',   // walnut
    glyph: 'feather',
    vibe: 'soft, retreating; not-yet-resolved',
  },

  // Bucket: directive — calm authority. Antique gold on slate. The
  // strategic / commanding register. (Previously: `strategic`.)
  directive: {
    wash: '#2a3a4f',   // deep slate
    surface: '#faf6ef',   // warm parchment
    accent: '#b78a3a',   // antique gold
    glyph: 'ampersand',
    vibe: 'calm, formal, chess-set; long shadows, gold-leaf accents',
  },

  // Bucket: cautious — measured, guarded. Pale mint-sage as guarded-quiet.
  // (Previously occupied by `relieved`.)
  cautious: {
    wash: '#b8c5b0',   // pale mint-sage
    surface: '#f6f8f2',   // off-white
    accent: '#4a6855',   // pine
    glyph: 'wave',
    vibe: 'measured, guarded; quiet caution',
  },

  // Bucket: sarcastic — playful, sly half-smile. (Previously: `teasing`.)
  sarcastic: {
    wash: '#c98989',   // rose dust
    surface: '#fbeae7',   // blush cream
    accent: '#82373a',   // wine
    glyph: 'curl',
    vibe: 'playful, mischievous; a sly half-smile',
  },

  // Bucket: intense — high-energy, urgent. Amber-smoke. (Previously:
  // `frustrated`.)
  intense: {
    wash: '#c08a3a',   // amber smoke
    surface: '#f4ecdf',   // warm grey-cream
    accent: '#7a3e16',   // rust
    glyph: 'flame',
    vibe: 'hotter than negative; smoke rather than flame',
  },

  // Bucket: surprise — caught off-guard. Oxblood spark for real astonishment.
  // (Previously occupied by `testy`'s oxblood treatment.)
  surprise: {
    wash: '#9a6b5a',   // dried-clay rose
    surface: '#f4eae3',   // warm bone
    accent: '#6e2a1a',   // oxblood
    glyph: 'sparks',
    vibe: 'short, punctuated; real astonishment',
  },

  // Sentinel: uncategorised — visually identical to neutral. Server emits
  // this id when the raw author-emotion didn't resolve to any of the 9
  // buckets. The panel keeps reading; the long tail just blends in.
  uncategorised: {
    wash: '#a8a59c',   // = neutral.wash
    surface: '#f7f4ee',   // = neutral.surface
    accent: '#3a3833',   // = neutral.accent
    glyph: 'circle',
    vibe: 'long-tail fallback; visually equal to neutral',
  },
};

// -----------------------------------------------------------------------------
// Legacy alias map — keep the dev-only mockup fixtures rendering unchanged
// after the Fork-2 re-key. ANY raw author-emotion label that the previous
// palette accepted as a top-level key now routes through here. The keys here
// are lowercased; `getPalette()` lowercases input before lookup.
//
// New code on the production path should NOT rely on this map — the server
// hands the client the bucket id directly. This is purely a back-compat
// shim for the dev-only `HoverPanelMockup` fixtures.
// -----------------------------------------------------------------------------
const LEGACY_LABEL_TO_BUCKET = {
  // direct legacy palette keys
  strategic: 'directive',
  grouchy: 'negative',
  frustrated: 'intense',
  supportive: 'positive',
  informative: 'neutral',
  teasing: 'sarcastic',
  apologetic: 'negative',
  testy: 'negative',
  relieved: 'positive',
  // common raw labels that show up in mockCharacters / Cambitter's corpus
  irritated: 'negative',
  sarcastic: 'sarcastic',
  directive: 'directive',
  cautious: 'cautious',
  intense: 'intense',
  surprise: 'surprise',
  surprised: 'surprise',
  uncertain: 'uncertain',
  negative: 'negative',
  positive: 'positive',
  // Server-emitted `label` value for the uncategorised bucket (server's
  // affectVocabulary.js PALETTE_BY_ID — bucket id 'uncategorised', label
  // 'varied'). Routes to the uncategorised palette entry so a character
  // whose top bucket is the long-tail sentinel still gets the correct
  // palette/glyph lookup. (Visually identical to neutral by design, but
  // the routing is now explicit rather than relying on the
  // fall-through default.) Added 2026-05-19 with the pill-row
  // terminology unification (envelopeToPanelShape now prefers `label`
  // over `paletteId`, so this alias matters for the first time).
  varied: 'uncategorised',
};

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

/**
 * Returns the palette entry for a given affect string. Accepts EITHER a
 * canonical bucket id (the production path) OR a legacy raw label (the
 * dev-only mockup path). Falls back to `neutral` if neither resolves —
 * keeps the surface legible for any newly-extracted affect the panel
 * hasn't been taught about yet.
 *
 * @param {string} affect
 * @returns {object} palette entry { wash, surface, accent, glyph, vibe }
 */
export function getPalette(affect) {
  if (typeof affect !== 'string' || !affect) return affectPalette.neutral;
  const key = affect.trim().toLowerCase();

  // Direct hit on canonical bucket id (production path).
  if (affectPalette[key]) return affectPalette[key];

  // Legacy raw-label fallback (dev-only fixtures).
  const bucket = LEGACY_LABEL_TO_BUCKET[key];
  if (bucket && affectPalette[bucket]) return affectPalette[bucket];

  return affectPalette.neutral;
}

// -----------------------------------------------------------------------------
// Driving-affect demotion (operator decision 2026-05-19)
// -----------------------------------------------------------------------------
// Most characters in the prototype corpus have `neutral` as their literal
// top-by-charCount affect bucket — narrative-style baselines, mid-emotion
// dialogue, and uncategorisable lines all aggregate there. If we anchor the
// panel's LAF (wash, accent, corner-motif glyph) on `dominantAffects[0]`
// when [0].emotion === 'neutral', every card ends up wearing the same
// greige-and-circle costume regardless of how the character actually
// sounds. The factual pill row still ranks neutral first (that's the
// truth), but the *stylistic* surfaces should reflect the character's
// distinguishing flavour, which is the next bucket down.
//
// Rule: when the top row's emotion is `neutral` AND a second row exists,
// the SECOND row is the "driving" row for any LAF/motif decision. Strict
// `neutral`-only trigger for now; `uncategorised` may be folded in later
// if a future pass surfaces the same complaint there.
//
// The helper is exported so HoverPanelMockup (corner motif glyph) and any
// future surfaces can read the same answer. The headline NL string is
// driven by an equivalent rule SERVER-SIDE in `nlTemplates.renderAffectPhrase`
// so the prose ("Mostly _____ and _____") and the panel chrome agree on
// the picked driver without the client having to rewrite the sentence.

/**
 * Pick the row that should drive stylistic surfaces (palette wash, accent,
 * corner-motif glyph). Returns the same row reference as the input (no
 * mutation, no copy).
 *
 * @param {Array<{emotion: string, charCount?: number}>} rows  Descending by
 *   charCount (the contract from the server-side compressed-affect emit).
 * @returns {object | null}  The driving row, or null if rows is empty.
 */
export function pickDrivingAffect(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  if (rows[0] && rows[0].emotion === 'neutral' && rows.length > 1) {
    return rows[1];
  }
  return rows[0];
}

/**
 * Resolve the dominant + secondary palette pair for a character's affect-row
 * set. Used by HoverPanelMockup to produce the dual-tone (wash from driving
 * affect, accent borrowed from the next-most-dominant row after the driver)
 * look on co-dominant characters.
 *
 * Input: array of `{ emotion, charCount }`, descending by charCount.
 * Output: { primary: <palette>, accentOverride: <accent-color | null> }
 *
 * Driving-affect demotion (see `pickDrivingAffect` above) applies BEFORE
 * the co-dominant math runs: if rows[0] is `neutral` and rows.length ≥ 2,
 * the dual-tone "primary" is rows[1] and the dual-tone "secondary" used
 * for the co-dominant ratio is rows[2] (so neutral can't sneak back in as
 * the accent either). When demotion does not trigger, the original
 * primary=rows[0]/secondary=rows[1] math runs unchanged.
 *
 * Co-dominant rule (unchanged from the design doc): if
 * secondary.charCount / primary.charCount is between 0.5 and 1.0 AND
 * secondary.charCount ≥ 100, the secondary's accent overrides the
 * primary's accent. Otherwise the primary palette wins entirely.
 */
export function resolveDualTone(rows) {
  if (!rows || rows.length === 0) {
    return { primary: affectPalette.neutral, accentOverride: null };
  }

  // Demotion: if rows[0] is neutral and a second row exists, shift the
  // driver index to 1. The secondary (for the co-dominant ratio) is then
  // the next row after the driver. Keeps neutral out of both LAF slots.
  const demoted = rows[0].emotion === 'neutral' && rows.length > 1;
  const driverIndex = demoted ? 1 : 0;
  const secondaryIndex = driverIndex + 1;

  const driver = rows[driverIndex];
  const top = getPalette(driver.emotion);

  if (rows.length <= secondaryIndex) {
    return { primary: top, accentOverride: null };
  }
  const second = rows[secondaryIndex];
  const driverChars = Math.max(driver.charCount || 0, 1);
  const ratio = (second.charCount || 0) / driverChars;
  const qualifiesAsCoDominant =
    ratio >= 0.5 && ratio <= 1.0 && (second.charCount || 0) >= 100;
  return {
    primary: top,
    accentOverride: qualifiesAsCoDominant ? getPalette(second.emotion).accent : null,
  };
}

export default affectPalette;
